import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { PublicRouteConfig, RouteConfig } from '@/config/routes.config';
import { ProtectedRoute } from './ProtectedRoute';
import { RouteLoader } from './RouteLoader';

interface RouteRendererProps {
    protectedRoutes: RouteConfig[];
    publicRoutes: PublicRouteConfig[];
}

export const RouteRenderer: React.FC<RouteRendererProps> = ({ protectedRoutes, publicRoutes }) => {
    const renderProtectedRoutes = () => {
        // Group protected routes by layout
        const groupedByLayout = protectedRoutes.reduce((acc, route) => {
            const LayoutComponent = route.layout || Layout;
            const layoutName = LayoutComponent.displayName || LayoutComponent.name || 'DefaultLayout';
            if (!acc[layoutName]) acc[layoutName] = { LayoutComponent, routes: [] };
            acc[layoutName].routes.push(route);
            return acc;
        }, {} as Record<string, { LayoutComponent: React.ComponentType<any>; routes: RouteConfig[] }>);

        // Render groups as parent <Route> elements with <Outlet>
        return Object.entries(groupedByLayout).map(([key, { LayoutComponent, routes }]) => (
            <Route
                key={key}
                element={
                    <ProtectedRoute>
                        <LayoutComponent />
                    </ProtectedRoute>
                }
            >
                {routes.map(({ path, component: Component }) => (
                    <Route key={path} path={path} element={<Component />} />
                ))}
            </Route>
        ));
    };

    const renderPublicRoutes = () =>
        publicRoutes.map(({ path, component: Component }) => (
            <Route key={path} path={path} element={<Component />} />
        ));

    return (
        <RouteLoader>
            <Routes>
                {renderPublicRoutes()}
                {renderProtectedRoutes()}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
        </RouteLoader>
    );
};
